import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import MainContainer from "../../../components/MainContainer";
import { headerData } from "../../../data/modules/dashboard-header";
import {
  AppBar,
  Autocomplete,
  Button,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  Grid,
  MenuItem,
  Skeleton,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { AntTab, AntTabs } from "../../../components/ant-tab";
import Spacer from "../../../components/Spacer";
import { Dialog, makeStyles, Paper, Tab, TableContainer, TableFooter } from "@material-ui/core";
import { hasPrivilege } from "../../../utils/privilege";
import SearchIcon from "@mui/icons-material/Search";
import { userDetailContext } from "../../../App";
import TimeTrackingService from "../../../services/TimeTrackingService";
import FolderZipIcon from '@mui/icons-material/FolderZip';
import ServiceService from "../../../services/ServiceService";
import ImmobilisationService from "../../../services/ImmobilisationService";
import Message from "../../tools/Message";
import FonctionService from "../../../services/FonctionService";
import LoadingSimpleBackdrop from "../../tools/LoadingSimpleBackdrop";
import LoadingTexteBackdrop from "../../tools/LoadingTexteBackdrop";
import TimeTrackingEtatFunding from "./TimeTrackingEtatFunding";

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
    borderRadius: 12,
    // marginTop: "2%",
    height: "60%",
    background: "linear-gradient(180deg, #ffffff 0%, #f6f8fb 100%)",
    border: "1px solid #e1e6ee",
    boxShadow: "0 8px 18px rgba(24, 39, 75, 0.08)",
  },
  appBar: {
    borderRadius: 10,
    boxShadow: "0 6px 16px rgba(15, 23, 42, 0.12)",
  },
  
  tabRoot: {
    minHeight: 36,
    width: "60%",
    minWidth: '28vw',
    padding: theme.spacing(0.5, 2),
    color: "#52606d",
    transition: "all 180ms ease",
    "&:hover": {
      backgroundColor: "#eef4ff",
      color: "#1d4ed8",
    },
    "&.Mui-selected": {
      color: "#1d4ed8",
      borderRadius: 8,
      borderColor: "#d6e2ff",
    },
  },
  labelPosition: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: "#3b4a5c",
    marginBottom: 4,
  },
  tableViewport: {
    height: 600,
    width: "100%",
    overflow: "auto",
    borderRadius: 10,
  },
  tableContainer: {
    borderRadius: 12,
    border: "1px solid #e0e6ef",
    backgroundColor: "#ffffff",
  },
  table: {
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: 1100,
  },
  cellStyle: {
    border: "1px solid #e2e6ee",
    fontSize: "13px!important",
    textAlign: "center",
    color: "#2f3a4c",
    backgroundColor: "#ffffff",
    padding: "6px 8px",
  },
  headerStyle: {
    borderRight: "1px solid #d9dee7",
    borderBottom: "1px solid #d9dee7",
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: "#eef2f7",
    fontWeight: "700 !important",
    textAlign: "center",
    color: "#243142",
    letterSpacing: 0.3,
  },
  bodyRow: {
    "&:nth-of-type(even)": {
      backgroundColor: "#fafbfc",
    },
    "&:hover": {
      backgroundColor: "#f0f6ff",
    },
  },
  stickyCell: {
    backgroundColor: "#ffffff",
  },
  tableFooter: {
    bottom: 0,
    width: "100%",
    backgroundColor: "#f3f5f9",
    borderTop: "2px solid #d6dbe4",
    position: "sticky",
    height: 56,
    zIndex: 2,
    fontWeight: 700,
  },
  emptyState: {
    textAlign: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: 500,
    color: "#b42318",
    fontWeight: 600,
  },
  emptyStateChip : {
    textAlign: "center",
    marginLeft: theme.spacing(54),
  },
  actionButton: {
    textTransform: "none",
    letterSpacing: 0.2,
    boxShadow: "0 6px 14px rgba(16, 24, 40, 0.12)",
    fontWeight: 600,
  },
  chip: {
    fontWeight: 600,
    backgroundColor: "#eef4ff",
    color: "#1d4ed8",
  },
  dialogPaper: {
    borderRadius: 14,
    border: "1px solid #e1e6ee",
    background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
    boxShadow: "0 12px 28px rgba(15, 23, 42, 0.2)",
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3),
  },
  dialogContentText: {
    color: "#344256",
    "& p": {
      margin: theme.spacing(1, 0),
    },
    "& strong": {
      color: "#1d4ed8",
      fontWeight: 700,
    },
  },
  dialogTitle: {
    margin: 0,
    marginBottom: theme.spacing(0.5),
    fontSize: 20,
    fontWeight: 700,
    color: "#b42318",
  },
  dialogMessage: {
    margin: 0,
    fontSize: 14,
    lineHeight: 1.6,
  },
  dialogActions: {
    padding: theme.spacing(1.5, 2),
    borderTop: "1px solid #e1e6ee",
    backgroundColor: "#f8fafc",
  },
  dialogActionButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    boxShadow: "none",
  },
  dialogPrimaryButton: {
    boxShadow: "0 8px 16px rgba(37, 99, 235, 0.2)",
  },
  dialogSecondaryButton: {
    borderColor: "#cbd5e1",
    color: "#475569",
  },
}));

const EMPTY_TOTALS = {
  leave_total: 0,
  leave_pending_total: 0,
  maternityLeave_total: 0,
  mission_total: 0,
  mission_pending_total: 0,
  manualTimeTracking_total: 0,
  pointage_manuel_en_attente_total: 0,
  present_total: 0,
  presenceFerie_total: 0,
  mission_ferie_total: 0,
  weekend_total: 0,
  absent_total: 0,
  total_days: 0,
};

const normalizeReportFilters = (sourceFilters) => ({
  directions: sourceFilters.directions.filter((dir) => dir !== 0),
  fonctions: sourceFilters.fonctions.filter((func) => func !== 0),
  services: sourceFilters.services.filter((serv) => serv !== 0),
  registration_number: sourceFilters.registration_number,
  nom: sourceFilters.nom,
  date_debut: sourceFilters.date_debut,
  date_fin: sourceFilters.date_fin,
});


export default function TimeTrackingEtat() {
  const classes = useStyles();
  const directionUser = localStorage.getItem("USER_DIR") || 0;
  const contextData = useContext(userDetailContext);
  const now = new Date();
  const begin = new Date(now.getFullYear(), now.getMonth() - 1, 20);// Le 20 du mois dernier
  const fifteenth = new Date(now.getFullYear(), now.getMonth(), 20);
  const end = now < fifteenth ? now : fifteenth;
  const initialFilters = {
    directions: [directionUser],
    services: [0],
    fonctions: [0],
    registration_number: "",
    nom: "",
    date_debut: formatDateLocal(begin),
    date_fin: formatDateLocal(end),
  };

  const [value, setValue] = useState(0);
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [activeSkeletonLoading, setActiveSkeletonLoading] = useState(false);
  const [directions, setDirections] = useState([]);
  const [services, setServices] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectAll, setSelectAll] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(() => normalizeReportFilters(initialFilters));
  const [totals, setTotals] = useState(EMPTY_TOTALS);
  const [openDialog, setOpenDialog] = useState({
    open: false,
    title: "Erreur de date",
    message: "La date de début ne peut pas être supérieure à la date de fin.",
  });
  const [message, setMessage] = useState({
    open: false,
    color: 'green',
    messageInfo: '',
  });
  const [ openDialogDownload, setOpenDialogDownload ] = useState(false);
  const [filtersVersion, setFiltersVersion] = useState(0); // incrémenté à chaque changement des filtres pour suivre les versions des données chargées
  const [loadedPresenceVersion, setLoadedPresenceVersion] = useState(-1); // version des filtres pour laquelle les données de présence ont été chargées
  const presenceRequestVersionRef = useRef(null);
  const selectedUsersForFunding = useMemo(
    () => (selectAll ? null : selectedUsers),
    [selectAll, selectedUsers]
  );
  const { open, color, messageInfo } = message;
   function handleCloseMessage(){
        setMessage({...message, open: false});
    }

    function formatDateLocal(date) {
      return date.toLocaleDateString('fr-CA'); 
    }
    const handleChangeTab = (newValue) => {
      setValue(newValue);
    };

      const fetchDirections = async () => {
        try {
            const result = await ImmobilisationService.direction();
            if (result.status) {
                setDirections(result.data);
            }
        } catch (error) {
            if (error.response) {
                setMessage({
                    open: true,
                    color: "red",
                    messageInfo: error.response.data.message,
                });
                console.error("Error fetching data:", error.response.data);
            } else {

                setMessage({
                    open: true,
                    color: "red",
                    messageInfo: error.message,
                });
                console.error("Error fetching data:", error.message);
            }
        } finally {
            // setOpenLoadingSimpleBackdrop(false);
        }
    };

  useEffect(() => {
    fetchDirections();
  }, []);

  useEffect(() => {
    if (value !== 0 || loadedPresenceVersion === filtersVersion || presenceRequestVersionRef.current === filtersVersion) {
      return;
    }

    const targetVersion = filtersVersion;
    presenceRequestVersionRef.current = targetVersion;
    let isMounted = true;

    const fetchCurrentPresenceTab = async () => {
      setActiveSkeletonLoading(true);
      setRows([]);
      setTotals(EMPTY_TOTALS);
      try {
        const result = await TimeTrackingService.getTimeTrackingReport(appliedFilters, []);

        if (!isMounted || presenceRequestVersionRef.current !== targetVersion) {
          return;
        }

        if (result.status) {
          const timeTrackingStatus = result.data?.timeTrackingStatus || [];
          setRows(
            timeTrackingStatus.map((row, index) => ({
              ...row,
              id: index + 1,
            }))
          );
          setTotals(result.data?.totals || EMPTY_TOTALS);
          setSelectedUsers(timeTrackingStatus.map((row) => row.user_id));
          setSelectAll(true);
          setLoadedPresenceVersion(targetVersion);
          setMessage({
            open: true,
            color: "green",
            messageInfo: "Données récupérées avec succès",
          });
        }
      } catch (error) {
        if (!isMounted || presenceRequestVersionRef.current !== targetVersion) {
          return;
        }

        if (error.response) {
          setMessage({
            open: true,
            color: "red",
            messageInfo: error.response.data.message,
          });
          console.error("Error fetching data:", error.response.data);
        } else {
          setMessage({
            open: true,
            color: "red",
            messageInfo: error.message,
          });
          console.error("Error fetching data:", error.message);
        }
      } finally {
        if (isMounted && presenceRequestVersionRef.current === targetVersion) {
          setActiveSkeletonLoading(false);
          presenceRequestVersionRef.current = null;
        }
      }
    };

    fetchCurrentPresenceTab();

    return () => {
      isMounted = false;
    };
  }, [appliedFilters, filtersVersion, loadedPresenceVersion, value]);

  useEffect(() => {
    if (value === 0 && loadedPresenceVersion === filtersVersion) {
      setActiveSkeletonLoading(false);
    }
  }, [filtersVersion, loadedPresenceVersion, value]);

    useEffect(() => {
          const fetchServices = async (direction_ids) => {
              if(direction_ids.length === 0) { // recuperer tous les services
                  ServiceService.getService().then((results) => {
                      if(results && results.data){
                          setServices(results.data)
                      }
                  })
              }

              if (direction_ids.length > 0) {
                  ServiceService.getServiceByDirectionIds(direction_ids).then((results) => {
                      if (results && results.data) {
                          setServices(results.data);
                      }
                  });
              }
          };
          fetchServices(filters.directions);
      }, [filters.directions]);

  useEffect(() => {
     const fetchFunctions = async (servicesIds) => {
     
    const servicesToSend = servicesIds.includes(0) ? services.map(service => service.service_id) : servicesIds;

         if (servicesToSend.length > 0) {
            FonctionService.getFunctionByMultipleService(servicesToSend).then((results) => {
                 if (results && results.data) {
                     setFunctions(results.data);
                 }
             });
         }
     };
     fetchFunctions(filters.services);
  }, [filters.services, services]);

  const handleChanges = (event) => {
    const { name, value } = event.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));

  }

  const handleSubmit = async (event) => { 
    event.preventDefault();

    if (filters.date_debut > filters.date_fin) {
        setOpenDialog({
          open: true,
          title: "Erreur de date",
          message: "La date de début ne peut pas être supérieure à la date de fin.",
        });
        return;
    }
    
    if (filters.date_fin > now.toISOString().split('T')[0]) {
        setOpenDialog({
          open: true,
          title: "Erreur de date",
          message: "La date de fin ne peut pas être supérieure à la date actuelle.",
        });
        return;
    }

    setSelectAll(true);
    setSelectedUsers([]);
    setAppliedFilters(normalizeReportFilters(filters));
    setFiltersVersion((prev) => prev + 1);
    return;
    // eslint-disable-next-line no-unreachable
    try {
      const filtersToSend = {
        directions : filters.directions.filter(dir => dir !== 0),
        fonctions : filters.fonctions.filter(func => func !== 0),
        services : filters.services.filter(serv => serv !== 0),
        registration_number: filters.registration_number,
        nom: filters.nom,
        date_debut: filters.date_debut,
        date_fin: filters.date_fin, 
      };
      const result = await TimeTrackingService.getTimeTrackingReport(filtersToSend);
      if (result.status) {
       
        setRows(result.data.timeTrackingStatus.map((row, index) => ({
          ...row,
          id: index + 1,
        }))
        );
        setTotals(                  
          result.data.totals
        );
        setSelectedUsers(result.data.timeTrackingStatus.map(row => row.user_id));
        setSelectAll(true);

      setMessage({
        open: true,
        color: "green",
        messageInfo: "Données récupérées avec succès",
      });

      }
    } catch (error) {
      if (error.response) {
        setMessage({
          open: true,
          color: "red",
          messageInfo: error.response.data.message,
        });
        console.error("Error fetching data:", error.response.data);
        setMessage({
          open: true,
          color: "red",
          messageInfo: error.response.data.message,
        });
      } else {
        setMessage({
          open: true,
          color: "red",
          messageInfo: error.message,
        });
        console.error("Error fetching data:", error.message);
      }
    } finally {
      setActiveSkeletonLoading(false);
    }
  }

  const downloadFile = (blob, fileName) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const handleGenerateFile = async (event) => {
    event.preventDefault();
    setOpenLoadingSimpleBackdrop(true);
    setOpenDialogDownload(false);
    try {
      const filtersToSend = normalizeReportFilters(filters);
      const result = await TimeTrackingService.generateTimeTrackingEtatFile(filtersToSend, !selectAll ? selectedUsers : []);

      if (result.status) {
         const blob = new Blob(
        [result.data || result], 
        { type: 'application/zip' }
      );
      const fileName = `RecapSituationPresence.zip`;
      downloadFile(blob, fileName);

        setMessage({
          open: true,
          color: "green",
          messageInfo: "Fichier généré avec succès",
        });
      }
    } catch (error) {
      if (error.response) {
        setMessage({
          open: true,
          color: "red",
          messageInfo: error.response.data.message,
        });
        console.error("Error fetching data:", error.response.data);
      } else {
        setMessage({
          open: true,
          color: "red",
          messageInfo: error.message,
        });
        console.error("Error fetching data:", error.message);
      }
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  }

  const handleSelectAllChange = (event, tabRows = rows) => {
    const isChecked = event.target.checked;
    setSelectAll(isChecked);
    if (isChecked) {
      setSelectedUsers(tabRows.map((row) => row.user_id));
    }
    else {
      setSelectedUsers([]);
    }
  }
  const handleCheckboxChange = (row, tabRows = rows) => {
    const isChecked = selectedUsers.some((m) => m === row.user_id);
    
    if (isChecked) {
      setSelectedUsers(selectedUsers.filter((m) => m !== row.user_id));
      setSelectAll(false);
    } else {
      setSelectedUsers([...selectedUsers, row.user_id]);
      // verify if all users are selected
      if (selectedUsers.length + 1 === tabRows.length) {
        setSelectAll(true);
      }
    }
  }

  const handleChangeService = (e, newValue) => {
    {
      e.preventDefault();
                  const isAllSelected = newValue.some(
                    (opt) =>
                      opt.service_id === 0 &&
                      opt.direction_id === 0 &&
                      opt.description === "Tous les services"
                  );
 
                  if (isAllSelected) {
                    if (filters.services.length === services.length) {
                      setFilters((prev) => ({ ...prev, services: [] }));
                  //   } else {
                      // Sélectionner tout
                      setFilters((prev) => ({
                        ...prev,
                        services: services.map((s) => s.service_id),
                      }));
                    }
                  } else {
                    const selectedServiceIds = newValue.map((service) => service.service_id);
                    setFilters((prevFilters) => ({
                      ...prevFilters,
                      services: selectedServiceIds,
                    }));
                  }
                }
              }

  const renderSkeletonRows = (cellsLength) => {
    const skeletonRows = Array.from({ length: 6 });
    const skeletonCells = Array.from({ length: cellsLength ?? 20 });

    return skeletonRows.map((_, rowIdx) => (
      <TableRow key={`skeleton-${rowIdx}`}>
        {skeletonCells.map((_, cellIdx) => {
          let stickyStyles = {};
          const isSticky = cellIdx === 0 || cellIdx === 4;

          if (cellIdx === 0) {
            stickyStyles = {
              left: 0,
              zIndex: 1,
              position: "sticky",
              backgroundColor: "#ffffff",
              width: "8%",
            };
          } else if (cellIdx === 1) {
            stickyStyles = { width: "8%" };
          } else if (cellIdx === 4) {
            stickyStyles = {
              left: 60,
              zIndex: 1,
              position: "sticky",
              backgroundColor: "#ffffff",
              width: 200,
            };
          }

          return (
            <TableCell
              key={`skeleton-cell-${rowIdx}-${cellIdx}`}
              className={isSticky ? `${classes.cellStyle} ${classes.stickyCell}` : classes.cellStyle}
              sx={stickyStyles}
            >
              <Skeleton animation="wave" width="100%" height={24} />
            </TableCell>
          );
        })}
      </TableRow>
    ));
  };

  return (
    <MainContainer {...headerData}>
    <Message open={open} color={color} messageInfo={messageInfo} handleClose={handleCloseMessage} />
      <LoadingTexteBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop || activeSkeletonLoading} />
      <Grid container spacing={2}>
     <Grid item xs={12} sm={6} md={3} style={{display: 'flex'}}>
          {
            !selectAll ? (
              <Chip label={`${selectedUsers.length} lignes selectionnées à exporter`} color="info" className={classes.chip} />
            ) : null
          }
          </Grid>
      <Grid container item xs={12}  md={9} justifyContent={"flex-end"}  alignContent={"right"} > 
        <Spacer x={2} />

        <Spacer x={2} />
        <Button
          variant="contained"
          color="success"
          onClick={() => {
            if( filters.date_debut > filters.date_fin) {
                setOpenDialog({
                  open: true,
                  title: "Erreur de date",
                  message: "La date de début ne peut pas être supérieure à la date de fin.",
                });
                return;
              }
              if(!selectAll && selectedUsers.length === 0) {
                setOpenDialog({
                  open: true,
                  title: "Erreur de sélection",
                  message: "Veuillez sélectionner au moins un utilisateur.",
                });
                return;
              }
            setOpenDialogDownload(true)}}
          startIcon={<FolderZipIcon />}
          className={classes.actionButton}
          disabled={!activeSkeletonLoading && rows.length === 0}
          style={{ marginLeft: "10px", marginRight: "10px" }}
          >
            Exporter
        </Button>
         <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          startIcon={<SearchIcon />}
          className={classes.actionButton}
        >
          Rechercher
        </Button>
      </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} style={{ marginTop: "auto" }}>
          <label className={classes.labelPosition}>Direction:</label>
          <FormControl fullWidth variant="outlined" size="small">
         <Autocomplete
              multiple
              limitTags={3}
              fullWidth
              options={
                hasPrivilege(contextData.privileges, "timetracking_report_all")
                  ? [{ direction_id: 0, name: "Toutes les directions", code: "Tous" }, ...directions]
                  : directions
              }
              getOptionLabel={(option) => `${option.code}`}
              value={directions.filter((dir) => filters.directions.includes(dir.direction_id))}
              onChange={(event, newValue) => {
                const selectedDirectionIds = newValue.map((dir) => dir.direction_id);
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  directions: selectedDirectionIds.length > 0 ? selectedDirectionIds : [0],
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={4} style={{ marginTop: "auto" }}>
          <label className={classes.labelPosition}>Service:</label>
          <Spacer y={0.2}></Spacer>
        <Autocomplete
                multiple
                limitTags={2}
                options={
                  hasPrivilege(contextData.privileges, "timetracking_report_all")
                    ? [
                        {
                          service_id: 0,
                          code: "Toutes les services",
                          name: "Tous les services",
                          direction_id: 0,
                          description: "Tous les services",
                        },
                        // {
                        //   service_id: -1,
                        //   code: "Directeurs centraux",
                        //   name: "Directeurs centraux",
                        //   direction_id: 0,
                        //   description: "Directeurs centraux",
                        // },
                        ...services,
                      ]
                    : [
                        {
                          service_id: 0,
                          code: "Toutes les services",
                          name: "Tous les services",
                          direction_id: -1,
                          description: "Tous les services",
                        },
                        ...services,
                      ]
                }
                getOptionLabel={(option) => option.code}
                value={[
                  // cas "Tous les services"
                  ...(filters.services.includes(0)
                    ? [
                        {
                          service_id: 0,
                          code: "Toutes les services",
                          name: "Tous les services",
                          direction_id: 0,
                          description: "Tous les services",
                        },
                      ]
                    : []),

                  // // cas "Directeurs centraux"
                  // ...(filters.services.includes(-1)
                  //   ? [
                  //       {
                  //         service_id: -1,
                  //         code: "Directeurs centraux",
                  //         name: "Directeurs centraux",
                  //         direction_id: 0,
                  //         description: "Directeurs centraux",
                  //       },
                  //     ]
                  //   : []),

                  // les autres services normaux
                  services.filter((s) => filters.services.includes(s.service_id)),
                ].flat()}
                onChange={(event, newValue) => handleChangeService(event, newValue)}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" size="small" />
                )}
              />

        </Grid>
         <Grid item xs={12} sm={6} md={4} style={{ marginTop: "auto" }}>
          <label className={classes.labelPosition}>Fonction:</label>
          <Spacer y={0.2}></Spacer>
          <FormControl fullWidth variant="outlined" size="small">
          <Autocomplete
            multiple
            options={[{ function_id: 0, code: "Toutes les fonctions", name: "" }, ...functions]}
            limitTags={3}
            getOptionLabel={(option) => option.code +  (option.name ? " - " + option.name : "")}
            value={filters.fonctions.includes(0)
              ? [{ function_id: 0, code: "Toutes les fonctions", name: "" }]
              : functions.filter((func) => filters.fonctions.includes(func.function_id))}
            onChange={(event, newValue) => {
              const isAllSelected = newValue.some((opt) => opt.function_id === 0);

              if (isAllSelected) {
                if (filters.fonctions.length === functions.length) {
                  setFilters((prev) => ({ ...prev, fonctions: [] }));
                } else {
                  // Sélectionner tout
                  setFilters((prev) => ({
                    ...prev,
                    fonctions: functions.map((f) => f.function_id),
                  }));
                }
              } else {
                const selectedFunctionIds = newValue.map((func) => func.function_id);
                setFilters((prevFilters) => ({
                  ...prevFilters,
                  fonctions: selectedFunctionIds.length > 0 ? selectedFunctionIds : [0],
                }));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                size="small"
                // style={{ width: "300px" }}
              />
            )}
          />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <label className={classes.labelPosition}>Du:</label>
          <Spacer y={0.2}></Spacer>
          <TextField
            id="outlined-basic"
            size="small"
            variant="outlined"
            defaultValue={filters.date_debut}
            name="date_debut"
            onChange={handleChanges}
            type="date"
            style={{ width: "300px" }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <label className={classes.labelPosition}>Au:</label>
          <Spacer y={0.2}></Spacer>
          <TextField
            id="outlined-basic"
            size="small"
            variant="outlined"
            name="date_fin"
            defaultValue={filters.date_fin}
            onChange={handleChanges}
            type="date"
            style={{ width: "300px" }}
          />
        </Grid>
          <Grid item xs={12} sm={6} md={3} style={{ marginTop: "auto" }}>
          <label className={classes.labelPosition}>Noms et prénom(s):</label>
          <Spacer y={0.2}></Spacer>
          <TextField
            id="outlined-basic"
            size="small"
            variant="outlined"
            name="nom"
            onChange={handleChanges}
            type="text"
            style={{ width: "300px" }}
          />
        </Grid>
         <Grid item xs={12} sm={6} md={3}>
          <label className={classes.labelPosition}>Immatricule:</label>
          <Spacer y={0.2}></Spacer>
          <TextField
            id="outlined-basic"
            size="small"
            variant="outlined"
            // defaultValue={filters.registration_number}
            name="registration_number"
            onChange={handleChanges}
            type="text"
            style={{ width: "300px" }}
          />
        </Grid>
      </Grid>
  <Grid container xs={12}>
        <AppBar position="static" color="default" sx={{marginTop: 2}} className={classes.appBar}>
          <AntTabs
            value={value}
            variant="scrollable"
            scrollButtons="auto"
              onChange={(event, newValue) => handleChangeTab(newValue)}
            aria-label="scrollable auto tabs example"
          >
            <AntTab
              disableRipple
              label="Situation présence"
              className={classes.tabRoot}
            />
            <AntTab
              disableRipple
              label="Nombre des missions par financement"
              className={classes.tabRoot}
            />
          </AntTabs>
        </AppBar>
      </Grid>
      <Spacer y={1} />
      {
        value === 0 && (
        <Paper className={classes.paper} variant="outlined">
        
         <div className={classes.tableViewport}>

          <TableContainer
              className={classes.tableContainer}
              // component={Pap:er}
              style={{overflow: 'unset' }}
            >
            <Table className={classes.table} style={{fontSize: 10}}  size="small" 
            maxHeight={600}
            >
                {/* <TableHead sx={{fontSize: 8}} > */}
          <TableRow  style={{ position: 'sticky', top: 0, zIndex: 2, fontSize: 8 }} >
            <TableCell rowSpan={2} className={classes.headerStyle} sx={{left: 0, zIndex: 6, position: "sticky", width: "8%"}}>
              <Checkbox
                        // style={{left: 0,zIndex: 6, position: "sticky"}}
                        color="primary"
                        checked={selectAll}
                        onChange={(event) => handleSelectAllChange(event, rows)}
                        // onClick={(event) => event.stopPropagation()} // Empêche la sélection de la colonne
                      />
            </TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle} sx={{ width: "8%"}}>N°</TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle} sx={{width: "28%"}}>Direction</TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle}>Immatricule</TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle} style={{left: 60, zIndex: 6, position: "sticky", width: 200}}>Noms et prénoms</TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle} >Service</TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle} >Fonction</TableCell>
            <TableCell colSpan={2} className={classes.headerStyle} align="center">En congé</TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle} >En congé de maternité ou autres</TableCell>
            <TableCell colSpan={2} className={classes.headerStyle} align="center" >En mission</TableCell>
            <TableCell colSpan={2} className={classes.headerStyle} align="center">Pointage Manuel</TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle} >Présent</TableCell>
            <TableCell colSpan={2} className={classes.headerStyle} align="center">Présence Férié</TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle} >Week end férié</TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle}>Absent sans motif</TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle}>Total général</TableCell>
            <TableCell rowSpan={2} className={classes.headerStyle}>Observation</TableCell>
          </TableRow>

           <TableRow style={{ position: 'sticky', top: 35, zIndex: 2, fontSize: 8 }} maxHeight={100}>
            <TableCell className={classes.headerStyle}>Validé</TableCell>
            <TableCell className={classes.headerStyle} >En attente</TableCell>
            <TableCell className={classes.headerStyle} >Validé</TableCell>
            <TableCell className={classes.headerStyle}>En attente</TableCell>
            <TableCell className={classes.headerStyle}>Validé</TableCell>
            <TableCell className={classes.headerStyle} >En attente</TableCell>
            <TableCell className={classes.headerStyle}>Mission</TableCell>
            <TableCell className={classes.headerStyle} >Pointage</TableCell>
          </TableRow>
        {/* </TableHead> */}
        <TableBody>
          {activeSkeletonLoading && renderSkeletonRows()}
          {
            !activeSkeletonLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={15} className={classes.emptyState}>
                  <Chip className={classes.emptyStateChip} color="error" label="Aucune donnée disponible" />
                </TableCell>
              </TableRow>
            ) 
          }
          {!activeSkeletonLoading && rows.map((row) => (
            <TableRow key={row.id} className={classes.bodyRow}>
              <TableCell className={`${classes.cellStyle} ${classes.stickyCell}`} sx={{left: 0, zIndex: 1, position: "sticky", backgroundColor: "#ffffff", width: "8%"}}>
                  <Checkbox
                              checked={selectedUsers.some((m) => m === row.user_id) || selectAll}
                              color="primary"
                              onChange={() => handleCheckboxChange(row, rows)}
                              onClick={(event) => event.stopPropagation()} 
                            />
              </TableCell>
              <TableCell className={classes.cellStyle} sx={{ width: "8%"}}>{row.id}</TableCell>
              <TableCell className={classes.cellStyle} sx={{width: "28%"}}>{row.direction_code} </TableCell>
              <TableCell className={classes.cellStyle}>{row.registration_number}</TableCell>
              <TableCell className={`${classes.cellStyle} ${classes.stickyCell}`} style={{left: 60, zIndex: 1, position: "sticky", backgroundColor: "#ffffff", width: 200}}>
                {row.nom_prenoms}
              </TableCell>
              <TableCell className={classes.cellStyle}>{row.service_code}</TableCell>
              <TableCell className={classes.cellStyle}>{row.function_name}</TableCell>
              <TableCell className={classes.cellStyle}>{row.leave_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.leave_pending_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.maternityLeave_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.mission_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.mission_pending_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.manualTimeTracking_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.pointage_manuel_en_attente_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.present_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.mission_ferie_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.presenceFerie_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.weekend_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.absent_number}</TableCell>
              <TableCell className={classes.cellStyle}>{row.total_days}</TableCell>
              <TableCell className={classes.cellStyle} style={{color: 'blue'}}>{row.observation}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter className={classes.tableFooter}>
          <TableRow>
            <TableCell colSpan={7} className={classes.cellStyle} style={{textAlign: "right"}}><strong>Total</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.leave_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.leave_pending_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.maternityLeave_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.mission_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.mission_pending_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.manualTimeTracking_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.pointage_manuel_en_attente_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.present_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.mission_ferie_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.presenceFerie_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.weekend_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.absent_total}</strong></TableCell>
            <TableCell className={classes.cellStyle}><strong>{totals.total_days}</strong></TableCell>
            <TableCell className={classes.cellStyle}></TableCell>
          </TableRow>
        </TableFooter>
            </Table>
            </TableContainer>                        
            </div>
          
        </Paper>
      )}
      <TimeTrackingEtatFunding
        filters={appliedFilters}
        setMessage={setMessage}
        setOpenLoadingSimpleBackdrop={setOpenLoadingSimpleBackdrop}
        handleSelectAllChange={handleSelectAllChange}
        handleCheckboxChange={handleCheckboxChange}
        selectAll={selectAll}
        selectedUsers={selectedUsers}
        selectedUsersForFunding={selectedUsersForFunding}
        filtersVersion={filtersVersion}
        renderSkeletonRows={() => renderSkeletonRows(10)}
        valueTab={value}
      />
        <Dialog
          open={openDialog.open}
          onClose={() => setOpenDialog( { ...openDialog, open: false })}
          PaperProps={{ className: classes.dialogPaper }}
        >
            <DialogContent className={classes.dialogContent}>
          <DialogContentText className={classes.dialogContentText}>
              <h2 className={classes.dialogTitle}>{openDialog.title}</h2>
              <p className={classes.dialogMessage}>{openDialog.message}</p>
              </DialogContentText>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenDialog({ ...openDialog, open: false })}
                className={`${classes.dialogActionButton} ${classes.dialogPrimaryButton}`}
              >
                Fermer
              </Button>
              </DialogActions>
        </Dialog>

        <Dialog
          open={openDialogDownload}
          onClose={ () => setOpenDialogDownload(false)}
          PaperProps={{ className: classes.dialogPaper }}
        >
          <DialogContent className={classes.dialogContent}>
            <DialogContentText className={classes.dialogContentText}>
              <p className={classes.dialogMessage}>
                Vous êtes sur le point de générer un état de situation de présence du <strong> {filters.date_debut} </strong> au <strong>{filters.date_fin}</strong>
                {filters.direction_id !== 0 ? ` pour la direction ${directions.find(dir => dir.direction_id === filters.direction_id)?.name}` : ''}
              </p>
              <p className={classes.dialogMessage}>
                Cette action peut prendre un certain temps en fonction du nombre d'enregistrements à traiter.
                Voulez-vous continuer ?
              </p>
            </DialogContentText>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setOpenDialogDownload(false)}
              className={`${classes.dialogActionButton} ${classes.dialogSecondaryButton}`}
            >
              Annuler
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateFile}
              className={`${classes.dialogActionButton} ${classes.dialogPrimaryButton}`}
            >
              Continuer
            </Button>
              </DialogActions>  
          </Dialog>

    </MainContainer>
  );
}
